import { useState } from 'react';

import { TableSort } from 'components/WrappedComponents/Table/types';
import { backAlertMessage } from 'hooks/utils/backAlertMessage';
import { HookSearchTypeWithSort, SearchTypeWithSort } from 'types/searchType';

import { PaginationKey, usePaginationContext } from '../PaginationStoreContext';

/**
 * This hook return all utils to build and search data for a dashboard
 * DtoType: Type of dto used
 * FilterType: Type of filter object
 * SearchResultDto: Type of returned object by API
 *
 * @param hookSearch Hook wich send request and convert result to readable data by dashboard
 * @param defaultFilter Filter set when we load dashboard
 * @param defaultSort
 * @param keyPagination
 * @returns {
 *  fetchData: function used to fetch API data in dashboard
 *  pagination: current pagination in dashboard
 *  setPagination: useEffect pagination setter
 *  filter: current filter in dashboard
 *  setFilter: useEffect filter setter
 *  refreshDashboard: reload search result in dashboard
 * }
 */
export function useDashboardSearchWithSort<
  DtoType,
  FilterType,
  SortType extends string | undefined,
  SearchResultDto,
>(
  hookSearch: HookSearchTypeWithSort<
    DtoType,
    FilterType,
    SortType,
    SearchResultDto
  >,
  defaultFilter: FilterType,
  defaultSort: TableSort<SortType>,
  keyPagination: PaginationKey,
): SearchTypeWithSort<DtoType, FilterType, SortType> {
  const [pagination, setPagination] = usePaginationContext(keyPagination);

  const [filter, setFilter] = useState<FilterType>(defaultFilter);
  const [sort, setSort] = useState<TableSort<SortType>>(defaultSort);

  const [search, convertResultToFetchDataResult] = hookSearch(
    filter,
    sort,
    pagination,
  );

  return {
    fetchData: async () => {
      const result = await search().catch((e: Response) => {
        void backAlertMessage(e);
        throw e;
      });
      return convertResultToFetchDataResult(result);
    },
    pagination: pagination,
    setPagination: newPagination => {
      setPagination(newPagination);
    },
    filter: filter,
    setFilter: setFilter,
    sort: sort,
    setSort: setSort,
    refreshDashboard: () => {
      setFilter({ ...filter });
    },
  };
}
